import * as i0 from '@angular/core';
import { inject, EventEmitter, SecurityContext, forwardRef, Input, ViewChild, Output, ViewEncapsulation, Component, NgModule } from '@angular/core';
import { Validators, NG_VALUE_ACCESSOR, NG_VALIDATORS, ReactiveFormsModule } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i2 from 'ngx-summernote';
import { NgxSummernoteModule } from 'ngx-summernote';
import * as i3 from '@engineering11/ui-lib/e11-input-label';
import { E11InputLabelModule } from '@engineering11/ui-lib/e11-input-label';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { E11IconModule } from '@engineering11/ui-lib/e11-icon';

const formatOnPast = (e) => {
    var bufferText = ((e.originalEvent || e).clipboardData || window['clipboardData']).getData('Text');
    e.preventDefault();
    bufferText = bufferText.replace(/\r?\n/g, '<br>');
    setTimeout(() => {
        document.execCommand('insertHtml', false, bufferText);
    }, 10);
};
const regexForSanitization = /<\/*(?:applet|b(?:ase|gsound|link)|iframe|embed|frame(?:set)?|ilayer|l(?:ayer|ink)|meta|object|s(?:cript|tyle)|t(?:itle|extarea)|xml|.*onmouseover)[^>]*?>/gi;
const editorConfigOptions = {
    minimal: {
        toolbar: [
            ['misc', ['undo', 'redo']],
            ['font', ['bold', 'underline', 'strikethrough', 'superscript', 'subscript', 'clear']],
            // ['fontsize', []],
            ['para', ['style0', 'ul', 'ol', 'paragraph']],
            ['insert', []],
            ['cleaner', ['cleaner']],
        ],
        disableDragAndDrop: true,
        codeviewFilterRegex: regexForSanitization,
        // callbacks: {
        //   onPaste: formatOnPast,
        // },
    },
    verbose: {
        toolbar: [
            ['misc', ['undo', 'redo']],
            ['font', ['bold', 'underline', 'strikethrough', 'superscript', 'subscript', 'clear']],
            ['fontsize', ['fontsize']],
            ['para', ['style0', 'ul', 'ol', 'paragraph']],
            ['insert', []],
            ['cleaner', ['cleaner']],
        ],
        fontSizes: ['14', '16', '20', '28'],
        disableDragAndDrop: true,
        codeviewFilterRegex: regexForSanitization,
        // callbacks: {
        //   onPaste: formatOnPast,
        // },
    },
};

function cleanStringFromHTML(str) {
    return (str
        ?.replace(/(<([^>]+)>)/gi, '')
        .replace(/( )/, ' ')
        .replace(/&nbsp;/g, ' ') || '');
}
class E11TextEditorComponent {
    /**
     * Pass editor config or pick from pre configured intents
     */
    set toolBarIntent(value) {
        if (typeof value === 'string') {
            this.config = { ...editorConfigOptions[value || 'verbose'], height: this.height };
            return;
        }
        this.config = { ...value, height: this.height };
    }
    constructor(cdRef) {
        this.cdRef = cdRef;
        this._sanitizer = inject(DomSanitizer);
        this.name = 'textarea';
        this.id = 'text-editor'; // required to access summernote instance
        this.label = '';
        this.value = '';
        this.required = false;
        this.hasError = false;
        this.disabled = false;
        this.showCharCounter = false;
        this.height = 150;
        this.labelTextOptional = '(Optional)';
        this.valueChange = new EventEmitter();
        this.writing = new EventEmitter(); // Lets parent form know you are writing so 'enter' doesn't submit any form
        this.config = editorConfigOptions.verbose;
        this.cleanedCharCounter = 0;
    }
    ngAfterViewChecked() {
        this.updateCounter();
        this.cdRef.detectChanges();
    }
    updateCounter() {
        this.cleanedCharCounter = this.cleanStringFromHTML(this.value).length;
    }
    editorChange(value) {
        const tmpNode = this.contentWrapper.nativeElement;
        tmpNode.querySelectorAll('.note-editable  img').forEach((node) => node.remove());
        this.value = this._sanitizer.sanitize(SecurityContext.HTML, tmpNode.querySelector('.note-editable').innerHTML);
        this.cleanedCharCounter = this.cleanStringFromHTML(this.value).length;
        if (this.onChange) {
            this.onChange(this.value);
            this.onTouched();
        }
        this.valueChange.emit(this.value);
    }
    writeValue(value) {
        this.value = this._sanitizer.sanitize(SecurityContext.HTML, value);
        const tmpNode = this.contentWrapper.nativeElement;
        if (tmpNode.querySelector('.note-editable')) {
            tmpNode.querySelector('.note-editable').innerHTML = value;
            this.cleanedCharCounter = this.cleanStringFromHTML(value).length;
        }
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    validate(control) {
        const tmpNode = this.contentWrapper.nativeElement;
        const value = tmpNode.querySelector('.note-editable');
        let finalValidation = null;
        if (control.hasValidator(Validators.required) && (value?.innerHTML == '<p><br></p>' || control.value == '')) {
            finalValidation = {
                required: control.touched,
            };
        }
        if (this.maxCharLength && this.cleanedCharCounter > this.maxCharLength) {
            const maxLength = { requiredLength: this.maxCharLength, actualLength: this.cleanedCharCounter };
            finalValidation = finalValidation !== null ? { ...finalValidation, maxLength } : { maxLength };
        }
        // important to avoid ExpressChanged Error when using FormControlDirective that validates initially (Edit state)
        setTimeout(() => {
            this.hasError = finalValidation !== null;
        });
        return finalValidation;
    }
    cleanStringFromHTML(str) {
        return cleanStringFromHTML(str);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11TextEditorComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11TextEditorComponent, selector: "e11-text-editor", inputs: { name: "name", id: "id", label: "label", value: "value", required: "required", hasError: "hasError", disabled: "disabled", maxCharLength: "maxCharLength", showCharCounter: "showCharCounter", height: "height", tooltipConfig: "tooltipConfig", labelTextOptional: "labelTextOptional", toolBarIntent: "toolBarIntent" }, outputs: { valueChange: "valueChange", writing: "writing" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => E11TextEditorComponent),
                multi: true,
            },
            {
                provide: NG_VALIDATORS,
                useExisting: forwardRef(() => E11TextEditorComponent),
                multi: true,
            },
        ], viewQueries: [{ propertyName: "contentWrapper", first: true, predicate: ["contentWrapper"], descendants: true, static: true }], ngImport: i0, template: "<div class=\"e11-text-editor e11-font-skin-primary\">\n  <div\n    #contentWrapper\n    class=\"e11-relative\"\n    [ngClass]=\"{\n      'e11-text-skin-error e11-placeholder-red-300 has-error': hasError,\n      'focus:e11-ring-skin-secondary focus:e11-border-skin-secondary': !hasError\n    }\"\n  >\n    <e11-input-label\n      [for]=\"id\"\n      [label]=\"label\"\n      [required]=\"required\"\n      [disabled]=\"disabled\"\n      [hasError]=\"hasError\"\n      [tooltipConfig]=\"tooltipConfig\"\n      [labelTextOptional]=\"labelTextOptional\"\n    ></e11-input-label>\n\n    <textarea\n      (focus)=\"this.writing.emit(true)\"\n      (blur)=\"this.writing.emit(false)\"\n      [ngxSummernote]=\"this.config\"\n      [ngxSummernoteDisabled]=\"disabled\"\n      [value]=\"value\"\n      (summernoteModelChange)=\"editorChange($event)\"\n      [id]=\"id\"\n    ></textarea>\n    <div *ngIf=\"showCharCounter\" class=\"e11-w-full\">\n      <div class=\"e11-text-right e11-text-xs e11-mt-1\">{{ cleanedCharCounter }} / {{ maxCharLength }}</div>\n    </div>\n\n    <div *ngIf=\"hasError\" class=\"e11-absolute e11-inset-y-0 e11-right-0 e11-bottom-0 e11-pr-3 e11-flex e11-flex-col\">\n      <svg\n        class=\"e11-h-5 e11-w-5 e11-text-skin-error e11-mt-auto e11-mb-8\"\n        xmlns=\"http://www.w3.org/2000/svg\"\n        viewBox=\"0 0 20 20\"\n        fill=\"currentColor\"\n        aria-hidden=\"true\"\n      >\n        <path\n          fill-rule=\"evenodd\"\n          d=\"M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z\"\n          clip-rule=\"evenodd\"\n        />\n      </svg>\n    </div>\n  </div>\n</div>\n", styles: [""], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgxSummernoteDirective, selector: "[ngxSummernote]", inputs: ["ngxSummernote", "summernoteModel", "ngxSummernoteDisabled"], outputs: ["summernoteModelChange", "imageUpload", "mediaDelete", "blur"] }, { kind: "component", type: i3.E11InputLabelComponent, selector: "e11-input-label", inputs: ["label", "id", "for", "size", "labelClassOverrides", "required", "hasError", "disabled", "darkMode", "requiredMode", "labelTextOptional", "tooltipConfig"] }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11TextEditorComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-text-editor', encapsulation: ViewEncapsulation.None, providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => E11TextEditorComponent),
                            multi: true,
                        },
                        {
                            provide: NG_VALIDATORS,
                            useExisting: forwardRef(() => E11TextEditorComponent),
                            multi: true,
                        },
                    ], template: "<div class=\"e11-text-editor e11-font-skin-primary\">\n  <div\n    #contentWrapper\n    class=\"e11-relative\"\n    [ngClass]=\"{\n      'e11-text-skin-error e11-placeholder-red-300 has-error': hasError,\n      'focus:e11-ring-skin-secondary focus:e11-border-skin-secondary': !hasError\n    }\"\n  >\n    <e11-input-label\n      [for]=\"id\"\n      [label]=\"label\"\n      [required]=\"required\"\n      [disabled]=\"disabled\"\n      [hasError]=\"hasError\"\n      [tooltipConfig]=\"tooltipConfig\"\n      [labelTextOptional]=\"labelTextOptional\"\n    ></e11-input-label>\n\n    <textarea\n      (focus)=\"this.writing.emit(true)\"\n      (blur)=\"this.writing.emit(false)\"\n      [ngxSummernote]=\"this.config\"\n      [ngxSummernoteDisabled]=\"disabled\"\n      [value]=\"value\"\n      (summernoteModelChange)=\"editorChange($event)\"\n      [id]=\"id\"\n    ></textarea>\n    <div *ngIf=\"showCharCounter\" class=\"e11-w-full\">\n      <div class=\"e11-text-right e11-text-xs e11-mt-1\">{{ cleanedCharCounter }} / {{ maxCharLength }}</div>\n    </div>\n\n    <div *ngIf=\"hasError\" class=\"e11-absolute e11-inset-y-0 e11-right-0 e11-bottom-0 e11-pr-3 e11-flex e11-flex-col\">\n      <svg\n        class=\"e11-h-5 e11-w-5 e11-text-skin-error e11-mt-auto e11-mb-8\"\n        xmlns=\"http://www.w3.org/2000/svg\"\n        viewBox=\"0 0 20 20\"\n        fill=\"currentColor\"\n        aria-hidden=\"true\"\n      >\n        <path\n          fill-rule=\"evenodd\"\n          d=\"M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z\"\n          clip-rule=\"evenodd\"\n        />\n      </svg>\n    </div>\n  </div>\n</div>\n" }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }], propDecorators: { name: [{
                type: Input
            }], id: [{
                type: Input
            }], label: [{
                type: Input
            }], value: [{
                type: Input
            }], required: [{
                type: Input
            }], hasError: [{
                type: Input
            }], disabled: [{
                type: Input
            }], maxCharLength: [{
                type: Input
            }], showCharCounter: [{
                type: Input
            }], height: [{
                type: Input
            }], tooltipConfig: [{
                type: Input
            }], labelTextOptional: [{
                type: Input
            }], valueChange: [{
                type: Output
            }], writing: [{
                type: Output
            }], contentWrapper: [{
                type: ViewChild,
                args: ['contentWrapper', { static: true }]
            }], toolBarIntent: [{
                type: Input
            }] } });

class E11TextEditorModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11TextEditorModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11TextEditorModule, declarations: [E11TextEditorComponent], imports: [CommonModule, ReactiveFormsModule, NgxSummernoteModule, E11IconModule, E11InputLabelModule], exports: [E11TextEditorComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11TextEditorModule, providers: [provideHttpClient(withInterceptorsFromDi())], imports: [CommonModule, ReactiveFormsModule, NgxSummernoteModule, E11IconModule, E11InputLabelModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11TextEditorModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [E11TextEditorComponent],
                    exports: [E11TextEditorComponent],
                    imports: [CommonModule, ReactiveFormsModule, NgxSummernoteModule, E11IconModule, E11InputLabelModule],
                    providers: [provideHttpClient(withInterceptorsFromDi())],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { E11TextEditorComponent, E11TextEditorModule, cleanStringFromHTML, editorConfigOptions, formatOnPast, regexForSanitization };
